import { ScannerMode } from '../../../lib/contracts';

export const MIN_UPC_LEN = 8;
export const MAX_UPC_LEN = 14;

export const MAX_UPLOAD_MB = 6;
export const MAX_IMAGE_DIMENSION = 1920;
export const IMAGE_COMPRESSION_QUALITY = 0.85;

export function getCooldownForMode(mode?: ScannerMode): number {
  switch (mode) {
    case ScannerMode.INVENTORY_CREATE:
    case ScannerMode.UPC_LOOKUP:
      return 500;
    case ScannerMode.CUSTOMER_RETURN_SCAN:
      return 1500;
    default:
      return 1200;
  }
}

export function normalizeUpc(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function canonicalUpc(raw: string): string {
  const digits = normalizeUpc(raw);
  if (digits.length === 13 && digits.startsWith('0')) {
    return digits.slice(1);
  }
  if (digits.length === 14 && digits.startsWith('00')) {
    return digits.slice(2);
  }
  if (digits.length === 11) {
    return `0${digits}`;
  }
  return digits;
}

export function validateUpc(raw: string): { ok: true; upc: string } | { ok: false; upc: string } {
  const normalized = canonicalUpc(String(raw || '').trim());
  if (!normalized) return { ok: false, upc: '' };
  if (normalized.length < MIN_UPC_LEN || normalized.length > MAX_UPC_LEN) return { ok: false, upc: '' };
  return { ok: true, upc: normalized };
}

/**
 * Resizes and compresses an image from a given source to a JPEG data URL.
 */
export function compressAndResizeImage(
  imageSource: HTMLImageElement | HTMLVideoElement,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas context unavailable'));
      return;
    }

    const sourceWidth = 'videoWidth' in imageSource ? imageSource.videoWidth : imageSource.width;
    const sourceHeight = 'videoHeight' in imageSource ? imageSource.videoHeight : imageSource.height;

    let targetWidth = sourceWidth;
    let targetHeight = sourceHeight;

    if (targetWidth > maxWidth || targetHeight > maxHeight) {
      if (targetWidth / targetHeight > maxWidth / maxHeight) {
        targetWidth = maxWidth;
        targetHeight = Math.round((targetWidth / sourceWidth) * sourceHeight);
      } else {
        targetHeight = maxHeight;
        targetWidth = Math.round((targetHeight / sourceHeight) * sourceWidth);
      }
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    ctx.drawImage(imageSource, 0, 0, targetWidth, targetHeight);
    resolve(canvas.toDataURL('image/jpeg', quality));
  });
}

/** Reads a photo file and returns a resized JPEG data URL small enough for one API request. */
export function fileToCompressedJpegDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Unsupported file type. Please upload an image.'));
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      reject(new Error(`File too large. Max size is ${MAX_UPLOAD_MB}MB.`));
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      compressAndResizeImage(img, MAX_IMAGE_DIMENSION, MAX_IMAGE_DIMENSION, IMAGE_COMPRESSION_QUALITY)
        .then(resolve, reject)
        .finally(() => URL.revokeObjectURL(objectUrl));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to read image.'));
    };
    img.src = objectUrl;
  });
}
